import './index.css';
import { useEffect, useState } from 'react';
import Navbar from './components/Navbar';
import LeavesCanvas from './components/LeavesCanvas';
import CursorTrail from './components/CursorTrail';
import Footer from './components/Footer';
import Chatbot from './components/Chatbot';
import useSEO from './useSEO';

import HomePage from './pages/HomePage';
import ServicesPage from './pages/ServicesPage';
import TechnologiesPage from './pages/TechnologiesPage';
import AboutPage from './pages/AboutPage';
import PortfolioPage from './pages/PortfolioPage';
import ContactPage from './pages/ContactPage';
import VideosPage from './pages/VideosPage';
import BlogPage from './pages/BlogPage';
import BlogDetailPage from './pages/BlogDetailPage';
import Admin from './sections/Admin';

import WebsiteDevelopmentPage from './pages/services/WebsiteDevelopmentPage';
import ShopifyEcommercePage from './pages/services/ShopifyEcommercePage';
import DigitalMarketingPage from './pages/services/DigitalMarketingPage';
import SeoPage from './pages/services/SeoPage';
import GoogleAdsPage from './pages/services/GoogleAdsPage';
import MetaAdsPage from './pages/services/MetaAdsPage';
import SocialMediaManagementPage from './pages/services/SocialMediaManagementPage';
import GraphicDesignPage from './pages/services/GraphicDesignPage';
import AiSolutionsPage from './pages/services/AiSolutionsPage';
import CustomSoftwareDevelopmentPage from './pages/services/CustomSoftwareDevelopmentPage';

// Simple hash-based routing (no react-router needed)
function useRoute() {
  const [hash, setHash] = useState(window.location.hash);
  useEffect(() => {
    const onChange = () => setHash(window.location.hash);
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  return hash;
}

const routes = {
  '': HomePage,
  '#/': HomePage,
  '#/home': HomePage,
  '#/about': AboutPage,
  '#/services': ServicesPage,
  '#/technologies': TechnologiesPage,
  '#/services/website-development': WebsiteDevelopmentPage,
  '#/services/shopify-ecommerce': ShopifyEcommercePage,
  '#/services/digital-marketing': DigitalMarketingPage,
  '#/services/seo': SeoPage,
  '#/services/google-ads': GoogleAdsPage,
  '#/services/meta-ads': MetaAdsPage,
  '#/services/social-media-management': SocialMediaManagementPage,
  '#/services/graphic-design': GraphicDesignPage,
  '#/services/ai-solutions': AiSolutionsPage,
  '#/services/custom-software-development': CustomSoftwareDevelopmentPage,
  '#/portfolio': PortfolioPage,
  '#/videos': VideosPage,
  '#/blog': BlogPage,
  '#/contact': ContactPage,
};

function App() {
  const route = useRoute();

  // Keep <title>, meta description, canonical & OG tags in sync with the page
  useSEO(route);

  // Scroll to top whenever the page changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [route]);

  // Scroll reveal observer — re-attach on every page change
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) e.target.classList.add('visible');
      }),
      { threshold: 0.12 }
    );

    const scan = () => {
      document.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale')
        .forEach(el => observer.observe(el)); // re-observing same el is a no-op
    };

    // Initial scan once the new page's elements are in the DOM
    const t = setTimeout(scan, 50);

    // Re-scan whenever new elements appear later (e.g. images fetched
    // from the backend render AFTER the initial scan — without this
    // they stay invisible/blank)
    let debounce;
    const mo = new MutationObserver(() => {
      clearTimeout(debounce);
      debounce = setTimeout(scan, 80);
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      clearTimeout(t);
      clearTimeout(debounce);
      mo.disconnect();
      observer.disconnect();
    };
  }, [route]);

  // Admin route (no navbar/footer)
  if (route === '#/admin') {
    return <Admin />;
  }

  // Blog detail route: #/blog/<id> — not in the static `routes` map since
  // the id is dynamic, so it's matched separately here.
  const blogDetailMatch = route.match(/^#\/blog\/([^/]+)$/);

  const Page = blogDetailMatch ? null : (routes[route] || HomePage);

  return (
    <>
      <CursorTrail />
      <LeavesCanvas />
      <Navbar currentRoute={route} />
      {blogDetailMatch
        ? <BlogDetailPage key={route} postId={blogDetailMatch[1]} />
        : <Page key={route} />}
      <Footer />
      <Chatbot />
    </>
  );
}

export default App;
